import { c } from 'ttag';

import { refusalCode } from '../../graphql/refusalCode';

/**
 * Reading of the refusals of `openAccountToPortal` and `closeAccountToPortal`.
 *
 * It lives apart from the dialog because a refusal is not a failure of the screen:
 * each of these codes describes a **state of the data** the operator has to act on,
 * and the sentence that says what to do is the whole value of this file. Rendering
 * `error.message` instead would show a server sentence in a red box, which reads as
 * « ça a planté », and « ça a planté » is answered by clicking again, which is
 * exactly the wrong move for every code below.
 */

export interface Refusal {
    /** What is true of the account, stated as a fact. */
    title: string;
    /** What to do about it. Never « retry » unless retrying is genuinely the fix. */
    guidance: string;
    /**
     * The server's own sentence, kept when it carries something the guidance above
     * does not already say.
     *
     * Always on a code we could not read: an unknown refusal with no detail leaves the
     * operator with nothing to report. And, since PCG-110, on **read** codes whose
     * message holds a fact the front cannot hold: `inviteUser` is the case that
     * widened it, `formatError` stripping every extension but `code`, so the names of
     * the refused accounts travel in the text and only the first code travels at all.
     * See the note on `detail` in `users/onboarding/inviteRefusal.ts`.
     *
     * Attention, it is never a second copy of the guidance. Kept where it repeats what the
     * sentence above already says, it puts a log-register phrase under a message
     * written for an operator, and the two read as a disagreement.
     */
    detail?: string;
}

/** The refusal, told to the operator who has to unblock it. */
export const refusalOf = (error: unknown): Refusal => {
    switch (refusalCode(error)) {
        case 'ACCOUNT_HAS_PORTAL_USERS':
            return {
                title: c('Portal access refusal').t`This account still carries portal users.`,
                guidance: c('Portal access refusal')
                    .t`Closing it would take nobody's access away: no reading path checks whether an account is open. Revoke the memberships one by one — that is the gesture that cuts the access — then close the account.`,
            };
        case 'ACCOUNT_MANAGER_UNRESOLVED':
            return {
                title: c('Portal access refusal').t`No referring account manager resolves on this account.`,
                guidance: c('Portal access refusal')
                    .t`This is data to correct, not a call to retry: clicking again will refuse again. An account opened without one leaves its client in front of a contact block naming nobody, and their requests routed to the fallback mailbox. Assign an account manager, then open the account.`,
            };
        case 'FORBIDDEN_OUT_OF_SCOPE':
            return {
                title: c('Portal access refusal').t`This account is outside your portfolio.`,
                guidance: c('Portal access refusal')
                    .t`Opening an account is reserved to whoever carries it. Ask an administrator whose portfolio holds this account.`,
            };
        case 'INTERNAL_STAFF_ONLY':
            return {
                title: c('Portal access refusal').t`This gesture belongs to the back-office.`,
                guidance: c('Portal access refusal')
                    .t`Opening an account to the portal is a Freelance.com decision, so a client-side role cannot carry it whatever its permissions.`,
            };
        case 'MISSING_PERMISSION':
            return {
                title: c('Portal access refusal').t`Your role does not carry this gesture.`,
                guidance: c('Portal access refusal')
                    .t`Managing accounts is a back-office permission. Ask an administrator to open the account, or to grant you that permission.`,
            };
        case 'INTERNAL_ORGANIZATION_NOT_ALLOWED':
            return {
                title: c('Portal access refusal').t`Freelance.com is not a client account.`,
                guidance: c('Portal access refusal')
                    .t`The internal organisation does not open to the portal. Giving a colleague access goes through the collaborator journey instead.`,
            };
        case 'ORGANIZATION_NOT_FOUND':
            return {
                title: c('Portal access refusal').t`This account no longer exists.`,
                guidance: c('Portal access refusal')
                    .t`It has probably disappeared from the source since this screen was loaded. Reload the page to see the accounts as they are now.`,
            };
        case 'NOT_AUTHENTICATED':
            return {
                title: c('Portal access refusal').t`Your session no longer carries an active membership.`,
                guidance: c('Portal access refusal').t`Sign in again, then start the gesture over.`,
            };
        default:
            return {
                title: c('Portal access refusal').t`The server refused this action.`,
                guidance: c('Portal access refusal')
                    .t`This refusal is not one the screen knows how to read, so nothing here says what to do about it. The server's answer is below — it is what the support desk needs.`,
                // `String(error)` on an unknown throw rather than a blank: an empty
                // technical block reads as a screen that swallowed the answer.
                detail: error instanceof Error ? error.message : String(error),
            };
    }
};
